import { useState } from "react";
import { Phone, Lock, Eye, EyeOff, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminSettings() {
  const { user, applyUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPhone, setNewPhone] = useState(user?.phone || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const field =
    "w-full rounded-lg border border-slate-200 bg-slate-50 py-3 pl-11 pr-11 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100";
  const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const phoneChanged = newPhone && newPhone.replace(/\D/g, "") !== (user?.phone || "");
    const wantsPasswordChange = newPassword.length > 0;

    if (!phoneChanged && !wantsPasswordChange) {
      setError("Change your mobile number or enter a new PIN.");
      return;
    }
    if (wantsPasswordChange && newPassword !== confirmPassword) {
      setError("New PIN and confirmation do not match.");
      return;
    }

    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (phoneChanged) payload.new_phone = newPhone;
      if (wantsPasswordChange) payload.new_password = newPassword;
      const { data } = await api.put("/admin/credentials", payload);
      applyUser(data);
      setNewPhone(data.phone);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess("Admin credentials updated. Use your new mobile number and PIN the next time you log in.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings" className="max-w-lg">
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
        <p className="text-sm text-slate-600">
          Update your admin mobile number and PIN. For security, you must enter your current
          PIN to make changes. Your PIN is never shown here.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Current PIN</label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
            <input
              data-testid="settings-current-password"
              type={showCurrent ? "text" : "password"}
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current PIN"
              className={field}
            />
            <button type="button" onClick={() => setShowCurrent((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-500" aria-label="Toggle PIN">
              {showCurrent ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="h-px w-full bg-slate-100" />

        <div>
          <label className={labelCls}>Admin Mobile Number</label>
          <div className="relative">
            <Phone className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
            <input
              data-testid="settings-new-phone"
              type="tel"
              inputMode="numeric"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="New phone number"
              className={field.replace("pr-11", "pr-4")}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>New PIN <span className="font-normal text-slate-400">(leave blank to keep current)</span></label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
            <input
              data-testid="settings-new-password"
              type={showNew ? "text" : "password"}
              value={newPassword}
              inputMode="numeric"
              onChange={(e) => setNewPassword(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="At least 4 digits (e.g. 1234)"
              className={field}
            />
            <button type="button" onClick={() => setShowNew((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-500" aria-label="Toggle PIN">
              {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div>
          <label className={labelCls}>Confirm New PIN</label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
            <input
              data-testid="settings-confirm-password"
              type={showNew ? "text" : "password"}
              value={confirmPassword}
              inputMode="numeric"
              onChange={(e) => setConfirmPassword(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="Re-enter new PIN"
              className={field.replace("pr-11", "pr-4")}
            />
          </div>
        </div>

        {error && <p data-testid="settings-error" className="text-sm font-medium text-red-600">{error}</p>}

        {success && (
          <p data-testid="settings-success" className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm font-medium text-green-700">
            <CheckCircle2 className="h-4 w-4" /> {success}
          </p>
        )}

        <button
          data-testid="settings-save-button"
          type="submit"
          disabled={saving}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-70"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update Credentials"}
        </button>
      </form>
    </div>
  );
}
